/**
 * HomePage Component (Web Version)
 * Maintains identical business logic from Mobile-client-core HomeScreen.tsx
 * Only UI components are adapted for web - all service calls and state management preserved
 */

import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigation } from '../shared/hooks/useNavigation';
import { ROUTES } from '../shared/navigation';
import { 
  Scissors,
  User, 
  ArrowLeft, 
  MapPin,
  Calendar,
  CheckCircle,
  X,
  Eye,
  Loader2,
  ShoppingBag,
  Package,
  Repeat
} from 'lucide-react';
import ProductTabContent from '../components/ProductTabContent';
import SubscriptionTabContent from '../components/SubscriptionTabContent';
import BannerCarousel from '../components/BannerCarousel';
import Inspirations from '../components/Inspirations';
import ServiceDetailsBottomSheet from '../components/ServiceDetailsBottomSheet';

// Import services and stores (identical to mobile)
import { useAuthStore } from '../stores/authStore';
import useServicesStore from '../stores/servicesStore';
import { useAppSettings } from '../contexts/AppSettingsContext';
import { getUserProfile } from '../services/userService';
import { getCategories, getServicesByCategory } from '../services/categoryService';
import { getAvailableTimeSlots } from '../services/appointmentService';
import { buildAssetUrl } from '../utils/urlHelpers';
import { generateAvailableDates, formatDateForDisplay as formatDateUtil } from '../utils/dateUtils';
import { getProfessionalsForService } from '../services/professionalService';
import { buildServiceImages, getPrimaryServiceImage, hasServiceImages } from '../utils/imageUtils';
import { getServiceVariations as fetchServiceVariations } from '../services/serviceVariationsService.js';
import { ServiceVariations } from '../components/VariationChip';

// Helper functions (identical to mobile)
const formatDuration = (minutes) => {
  if (minutes === undefined || minutes === null || minutes <= 0) return '-';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : ''}`.trim() || '-';
};

const formatPrice = (priceStr) => {
  if (priceStr === undefined || priceStr === null) return 'R$ -';
  const priceNum = parseFloat(priceStr);
  if (isNaN(priceNum)) return 'R$ -';
  return `R$ ${priceNum.toFixed(2).replace('.', ',')}`;
};

const formatPriceWithEvaluation = (priceStr, isSubjectToEvaluation = false, hasVariations = false) => {
  if (priceStr === undefined || priceStr === null) return { price: 'R$ -', prefix: null };
  const priceNum = parseFloat(priceStr);
  if (isNaN(priceNum)) return { price: 'R$ -', prefix: null };
  
  const formattedPrice = `R$ ${priceNum.toFixed(2).replace('.', ',')}`;
  
  if (isSubjectToEvaluation) {
    return {
      price: formattedPrice,
      prefix: 'A partir de',
      isSubjectToEvaluation: true
    };
  }
  
  return {
    price: formattedPrice,
    prefix: null,
    isSubjectToEvaluation: false
  };
};

const getFullImageUrl = (relativePath) => {
  return buildAssetUrl(relativePath);
};

// buildServiceImages function now imported from imageUtils utility

const HomePageInner = ({ navigation }, ref) => {
  const location = useLocation();
  const { navigate } = useNavigation();
  const { displaySettings, isLoading: isLoadingSettings } = useAppSettings();

  // Build visible tabs based on settings (empty while loading to prevent flash)
  const visibleTabs = React.useMemo(() => {
    if (isLoadingSettings) return []; // Don't show any tabs while loading settings
    const tabs = [];
    if (displaySettings.showServicos) tabs.push({ id: 'servicos', label: 'Serviços', icon: Scissors });
    if (displaySettings.showProdutos) tabs.push({ id: 'produtos', label: 'Produtos', icon: Package });
    if (displaySettings.showAssinaturas) tabs.push({ id: 'assinaturas', label: 'Assinaturas', icon: Repeat });
    return tabs;
  }, [displaySettings, isLoadingSettings]);

  // All state variables (identical to mobile)
  const [currentScreen, setCurrentScreen] = useState('categories');
  const [activeTab, setActiveTab] = useState(0); // Index into visibleTabs array
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [observations, setObservations] = useState('');
  const [imagePopupVisible, setImagePopupVisible] = useState(false);
  const [selectedImageForPopup, setSelectedImageForPopup] = useState(null);
  const [serviceDetailsModalVisible, setServiceDetailsModalVisible] = useState(false);
  const [selectedServiceForModal, setSelectedServiceForModal] = useState(null);

  // Generate available dates (identical to mobile)
  const availableDates = generateAvailableDates(30);

  // All other state variables (identical to mobile)
  const [fetchedCategories, setFetchedCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categoryError, setCategoryError] = useState(null);
  const [fetchedServices, setFetchedServices] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [serviceError, setServiceError] = useState(null);
  const [fetchedProfessionals, setFetchedProfessionals] = useState([]);
  const [isLoadingProfessionals, setIsLoadingProfessionals] = useState(false);
  const [professionalsError, setProfessionalsError] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [timeSlotsError, setTimeSlotsError] = useState(null);

  // Service card expansion state (identical to mobile pattern)
  const [expandedServiceIds, setExpandedServiceIds] = useState(new Set());
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImageForModal, setSelectedImageForModal] = useState(null);
  
  // New state for service details bottom sheet
  const [isServiceDetailsSheetVisible, setIsServiceDetailsSheetVisible] = useState(false);
  const [selectedServiceForSheet, setSelectedServiceForSheet] = useState(null);
  
  // Image loading and error state management
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  
  // Service variations state
  const [serviceVariations, setServiceVariations] = useState({}); // { serviceId: [variationGroups] }

  // Auth and services store integration (identical to mobile)
  const { user, isAuthenticated, setProfile, logout: storeLogout } = useAuthStore((state) => ({
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    setProfile: state.setProfile,
    logout: state.logout,
  }));

  const {
    selectedServices,
    toggleService,
    clearServices,
    getTotalPrice,
    setServiceVariation,
    toggleServiceVariation,
    getServiceVariations,
    getServiceFinalPrice,
    getExpandedServicesCount,
    addServiceWithVariation,
    removeService
  } = useServicesStore((state) => ({
    selectedServices: state.selectedServices,
    toggleService: state.toggleService,
    clearServices: state.clearServices,
    getTotalPrice: state.getTotalPrice,
    setServiceVariation: state.setServiceVariation,
    toggleServiceVariation: state.toggleServiceVariation,
    getServiceVariations: state.getServiceVariations,
    getServiceFinalPrice: state.getServiceFinalPrice,
    getExpandedServicesCount: state.getExpandedServicesCount,
    addServiceWithVariation: state.addServiceWithVariation,
    removeService: state.removeService,
  }));

  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // ScrollView refs adapted for web
  const categoriesScrollRef = useRef(null);
  const servicesScrollRef = useRef(null);
  const serviceDetailsScrollRef = useRef(null);
  const schedulingScrollRef = useRef(null);
  const confirmationScrollRef = useRef(null);
  const ordersScrollRef = useRef(null);

  // All useEffect hooks (identical logic to mobile)
  useEffect(() => {
    const fetchProfile = async () => {
      // Skip profile fetch for guest users - they don't have a database profile
      if (user?.isGuest || user?.role === 'GUEST') {
        return;
      }

      if (isAuthenticated && user && !user.phone_number) {
        setIsProfileLoading(true);
        try {
          const rawProfileData = await getUserProfile();
          setProfile(rawProfileData);
        } catch (error) {
          console.error('Profile loading error:', error);
          console.error('Error details:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data,
            url: window.location.href,
            tenantInfo: error.tenantInfo
          });
          alert(`Não foi possível carregar os detalhes do seu perfil. Erro: ${error.message}`);
        } finally {
          setIsProfileLoading(false);
        }
      }
    };
    fetchProfile();
  }, [isAuthenticated, user?.id, user?.phone_number, user?.isGuest, user?.role]); // Use specific user properties instead of whole user object

  // Load categories function (identical to mobile)
  const loadCategories = async () => {
    try {
      setIsLoadingCategories(true);
      setCategoryError(null);
      const data = await getCategories();
      if (Array.isArray(data)) {
        const sortedData = data.sort((a, b) => {
          if (a.display_order !== undefined && b.display_order !== undefined) {
            if (a.display_order !== b.display_order) {
              return a.display_order - b.display_order;
            }
          }
          return a.name.localeCompare(b.name);
        });
        
        // Process category data
        sortedData.forEach(cat => {
          cat.processed_url = getFullImageUrl(cat.icon_url);
        });
        
        setFetchedCategories(sortedData);
      } else {
        setFetchedCategories([]);
      }
    } catch (err) {
      const errorMessage = typeof err.message === 'string' ? err.message : 'Failed to load categories.';
      setCategoryError(errorMessage);
      setFetchedCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  // Reset to categories screen when navigating to dashboard and reset to first tab
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      setCurrentScreen('categories');
      setActiveTab(0);
      setSelectedCategory(null);
      setExpandedServiceIds(new Set());
    }
  }, [location.pathname]);

  // Load variations for a service
  const loadServiceVariations = async (serviceId) => {
    try {
      const variations = await fetchServiceVariations(serviceId);
      setServiceVariations(prev => ({
        ...prev,
        [serviceId]: variations || []
      }));
    } catch (err) {
      // Silently fail - variations are optional
      setServiceVariations(prev => ({
        ...prev,
        [serviceId]: []
      }));
    }
  };

  // Load services function (identical to mobile)
  const loadServicesForCategory = async (categoryId) => {
    if (!categoryId) return;
    setIsLoadingServices(true);
    setServiceError(null);
    setFetchedServices([]);
    setServiceVariations({}); // Clear variations when loading new category
    try {
      const data = await getServicesByCategory(categoryId);
      if (Array.isArray(data)) {
        const services = data.map((service) => ({
          id: service.id,
          name: service.name,
          duration_minutes: service.duration_minutes,
          price: String(service.price),
          description: service.description,
          category_id: service.category_id,
          // New dynamic images array from ServiceImage table
          images: service.images || [],
          // Legacy static image fields for backward compatibility
          image: service.image,
          image_liso: service.image_liso,
          image_ondulado: service.image_ondulado,
          image_cacheado: service.image_cacheado,
          image_crespo: service.image_crespo,
          // Add price evaluation field
          price_subject_to_evaluation: service.price_subject_to_evaluation || false,
        }));
        
        setFetchedServices(services);
        
        // Load variations for each service
        services.forEach(service => {
          loadServiceVariations(service.id);
        });
      } else {
        setFetchedServices([]);
      }
    } catch (err) {
      const errorMessage = typeof err.message === 'string' ? err.message : 'Failed to load services for this category.';
      setServiceError(errorMessage);
      setFetchedServices([]);
    } finally {
      setIsLoadingServices(false);
    }
  };

  useEffect(() => {
    if (selectedCategory?.id) {
      loadServicesForCategory(selectedCategory.id);
    } else {
      setFetchedServices([]);
      setServiceError(null);
      setIsLoadingServices(false);
    }
  }, [selectedCategory]);


  // Helper function for scrolling (adapted for web)
  const scrollToTop = (scrollRef) => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Event handlers (identical logic to mobile)
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setCurrentScreen('services');
    setSelectedService(null);
    setTimeout(() => scrollToTop(servicesScrollRef), 0);
  };

  const handleBackToCategories = () => {
    setCurrentScreen('categories');
    setSelectedCategory(null);
    setTimeout(() => scrollToTop(categoriesScrollRef), 0);
  };

  // Retry handlers (identical to mobile)
  const retryLoadCategories = () => {
    loadCategories();
  };

  const retryLoadServices = () => {
    if (selectedCategory?.id) {
      loadServicesForCategory(selectedCategory.id);
    }
  };

  // Service card expansion handlers (only one card expanded at a time)
  const handleToggleServiceExpansion = (serviceId) => {
    setExpandedServiceIds(prev => {
      const newSet = new Set();
      // If the clicked service is already expanded, close it (empty set)
      // Otherwise, expand only the clicked service
      if (!prev.has(serviceId)) {
        newSet.add(serviceId);
        
        // Scroll to the expanded card after state update, accounting for fixed header
        setTimeout(() => {
          const cardElement = document.getElementById(`service-card-${serviceId}`);
          if (cardElement && servicesScrollRef.current) {
            const headerHeight = 120; // Account for fixed header height
            const cardRect = cardElement.getBoundingClientRect();
            const scrollTop = servicesScrollRef.current.scrollTop;
            const targetScrollTop = scrollTop + cardRect.top - headerHeight - 8; // 8px minimal padding
            
            servicesScrollRef.current.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
      return newSet;
    });
  };

  const isServiceExpanded = (serviceId) => {
    return expandedServiceIds.has(serviceId);
  };

  // Handle image modal
  const handleImagePress = (imageUrl) => {
    setSelectedImageForModal(imageUrl);
    setImageModalVisible(true);
  };

  // Image loading and error handlers
  const handleImageLoad = (imageKey) => {
    setImageLoadingStates(prev => ({ ...prev, [imageKey]: false }));
    setImageErrors(prev => ({ ...prev, [imageKey]: false }));
  };

  const handleImageError = (imageKey) => {
    setImageLoadingStates(prev => ({ ...prev, [imageKey]: false }));
    setImageErrors(prev => ({ ...prev, [imageKey]: true }));
  };

  const handleImageLoadStart = (imageKey) => {
    setImageLoadingStates(prev => ({ ...prev, [imageKey]: true }));
    setImageErrors(prev => ({ ...prev, [imageKey]: false }));
  };

  // Handle variation selection (single-choice groups)
  // Each variation click = add/remove from cart (same behavior as toggle)
  // This allows adding same service with different variations as separate cart items
  const handleVariationSelect = (serviceId, groupId, variation) => {
    const service = fetchedServices.find(s => s.id === serviceId);
    if (!service) return;

    if (variation === null) {
      // Deselecting - remove any cart items from this service/group
      const cartItemsToRemove = selectedServices.filter(s =>
        (s.originalServiceId || s.id) === serviceId &&
        s.selectedVariation?.groupId === groupId
      );
      cartItemsToRemove.forEach(item => {
        removeService(item.cartItemId || item.id);
      });
    } else {
      // Check if this exact variation is already in cart
      const cartItemId = `${serviceId}_${variation.id}`;
      const existsInCart = selectedServices.some(s => s.cartItemId === cartItemId);

      if (existsInCart) {
        // Remove from cart (clicking selected variation = deselect)
        removeService(cartItemId);
      } else {
        // Add as separate cart item
        addServiceWithVariation(service, variation, groupId);
      }
    }
  };

  // Handle variation toggle for multiple choice groups
  // Each variation becomes a separate cart item (same as single choice)
  const handleVariationToggle = (serviceId, groupId, variation) => {
    const service = fetchedServices.find(s => s.id === serviceId);
    if (!service || !variation) return;

    // Check if this exact variation is already in cart
    const cartItemId = `${serviceId}_${variation.id}`;
    const existsInCart = selectedServices.some(s => s.cartItemId === cartItemId);

    if (existsInCart) {
      // Remove from cart
      removeService(cartItemId);
    } else {
      // Add as separate cart item
      addServiceWithVariation(service, variation, groupId);
    }
  };

  // Helper function to generate variation preview text
  const getVariationPreview = (variations) => {
    if (!variations || !Array.isArray(variations) || variations.length === 0) return null;
    
    const allVariations = variations.flatMap(group => group.variations || []);
    if (allVariations.length === 0) return null;
    
    // Show first few variation names
    const preview = allVariations
      .slice(0, 3)
      .map(v => v.name)
      .join(', ');
    
    const totalCount = allVariations.length;
    if (totalCount > 3) {
      return `${preview} +${totalCount - 3} mais`;
    }
    
    return preview;
  };

  // Helper function to calculate price range for services with variations
  const getPriceRange = (basePrice, variations) => {
    if (!variations || !Array.isArray(variations) || variations.length === 0) return null;
    
    const allVariations = variations.flatMap(group => group.variations || []);
    if (allVariations.length === 0) return null;
    
    const priceDeltas = allVariations.map(v => parseFloat(v.price_delta || 0));
    
    // If no price deltas or all are zero, no range needed
    if (priceDeltas.every(delta => delta === 0)) return null;
    
    const minDelta = Math.min(...priceDeltas);
    const maxDelta = Math.max(...priceDeltas);
    
    const base = parseFloat(basePrice || 0);
    const minPrice = base + minDelta;
    const maxPrice = base + maxDelta;
    
    if (minPrice === maxPrice) return null; // No range if same price
    
    return { minPrice, maxPrice };
  };

  // Categories screen render
  const renderCategoriesScreen = () => (
    <div ref={categoriesScrollRef} className="h-full overflow-y-auto mobile-scroll bg-brand-light dark:bg-brand-dark">
      {/* Extended header background for overscroll */}
      <div className="bg-brand-dark dark:bg-brand-light" style={{ paddingTop: '50px', marginTop: '-50px' }}>
        {/* Header with luxury tabs */}
        <div className="safe-area-top px-6 py-6">
          {/* Loading skeleton for tabs */}
          {isLoadingSettings && (
            <div className="relative flex space-x-0 border border-brand-light/20 dark:border-brand-dark/20 animate-pulse">
              <div className="flex-1 flex flex-col items-center justify-center py-3">
                <div className="w-4 h-4 bg-brand-light/30 dark:bg-brand-dark/30 rounded"></div>
                <div className="mt-1.5 w-12 h-3 bg-brand-light/30 dark:bg-brand-dark/30 rounded"></div>
              </div>
            </div>
          )}
          {/* Luxury Tab Bar - Only show if more than 1 tab (otherwise pointless) */}
          {!isLoadingSettings && visibleTabs.length > 1 && (
            <div className="relative flex space-x-0 border border-brand-light/20 dark:border-brand-dark/20">
              {/* Slider indicator */}
              <div
                className="absolute top-0 bottom-0 bg-brand-light dark:bg-brand-dark transition-transform duration-300 ease-in-out z-0"
                style={{
                  width: `${100 / visibleTabs.length}%`,
                  transform: `translateX(${activeTab * 100}%)`
                }}
              ></div>

              {visibleTabs.map((tab, index) => {
                const TabIcon = tab.icon;
                const isMiddle = index > 0 && index < visibleTabs.length - 1;
                const hasBorder = visibleTabs.length > 1 && index > 0;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(index)}
                    className={`relative z-10 flex-1 flex flex-col items-center justify-center py-3 text-xs font-medium tracking-wider uppercase transition-colors duration-200 ${
                      hasBorder ? 'border-l border-brand-light/20 dark:border-brand-dark/20' : ''
                    } ${
                      activeTab === index
                        ? 'text-brand-dark dark:text-brand-light'
                        : 'text-brand-light/70 dark:text-brand-dark/70'
                    }`}
                  >
                    <TabIcon size={16} strokeWidth={1.5} />
                    <span className="mt-1.5">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-brand-light dark:bg-brand-dark -mt-4 px-4 sm:px-6 py-8 min-h-0 pb-20">
        {visibleTabs[activeTab]?.id === 'servicos' && (
          <>
            {/* Loading State */}
            {isLoadingCategories && (
              <div className="flex justify-center items-center py-20">
                <Loader2 size={32} className="spinner text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
              </div>
            )}

            {/* Error State */}
            {categoryError && (
              <div className="text-center py-20">
                <p className="text-red-600 dark:text-red-400 mb-6 font-sans">{categoryError}</p>
                <button
                  onClick={retryLoadCategories}
                  className="px-8 py-3 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium tracking-wider uppercase text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
                >
                  Tentar novamente
                </button>
              </div>
            )}

            {/* Banner Carousel - Shows only if 2+ active banners exist */}
            {!categoryError && <BannerCarousel />}

            {/* Categories Grid - Compact Apple-style Design */}
            {!isLoadingCategories && !categoryError && (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {fetchedCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className="bg-white dark:bg-brand-gray-800 rounded-xl p-3 flex flex-col items-center justify-center shadow-sm border border-brand-dark/5 dark:border-brand-light/10 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                  >
                    {/* Icon */}
                    <div className="w-12 h-12 flex items-center justify-center mb-2">
                      {category.icon_url ? (
                        <img
                          src={getFullImageUrl(category.icon_url)}
                          alt={category.name}
                          className="w-12 h-12 object-contain dark:invert dark:brightness-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className="w-12 h-12 flex items-center justify-center"
                        style={{ display: category.icon_url ? 'none' : 'flex' }}
                      >
                        <Scissors size={28} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
                      </div>
                    </div>

                    {/* Category Name */}
                    <h3 className="text-sm font-medium text-brand-dark dark:text-brand-light text-center leading-tight line-clamp-2">
                      {category.name}
                    </h3>
                  </button>
                ))}
              </div>
            )}

            {/* Inspirations Component - Shown below category buttons */}
            <div className="mt-8">
              <Inspirations />
            </div>
          </>
        )}

        {visibleTabs[activeTab]?.id === 'produtos' && <ProductTabContent />}

        {visibleTabs[activeTab]?.id === 'assinaturas' && <SubscriptionTabContent />}
      </div>
    </div>
  );

  // Service cart footer component - Luxury design
  const renderServiceCartFooter = () => {

    if (selectedServices.length === 0) return null;

    return (
      <div className="fixed left-0 right-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 p-4 safe-area-bottom z-50" style={{ bottom: '64px' }}>
        <div className="flex justify-between items-center mb-4">
          <span className="text-brand-dark dark:text-brand-light text-xs tracking-wider uppercase font-medium">
            Total ({getExpandedServicesCount()} {getExpandedServicesCount() === 1 ? 'serviço' : 'serviços'})
          </span>
          <span className="text-2xl font-serif font-bold text-brand-dark dark:text-brand-light">
            {formatPrice(getTotalPrice())}
          </span>
        </div>

        <button
          onClick={() => {
            navigate(ROUTES.CART);
          }}
          className="w-full bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark py-4 border border-brand-dark dark:border-brand-light font-medium text-sm tracking-widest uppercase hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
        >
          Continuar ({getExpandedServicesCount()})
        </button>
      </div>
    );
  };

  // Services screen render
  const renderServicesScreen = () => (
    <div className="h-full flex flex-col">
      {/* Fixed Header - Luxury Design */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-brand-dark dark:bg-brand-light">
        {/* Extended header background for overscroll */}
        <div className="bg-brand-dark dark:bg-brand-light" style={{ paddingTop: '50px', marginTop: '-50px' }}>
          {/* Header */}
          <div className="safe-area-top px-6 py-4 flex items-center border-b border-brand-light/20 dark:border-brand-dark/20">
            <button
              onClick={handleBackToCategories}
              className="mr-4 p-2 flex items-center justify-center hover:bg-brand-light/10 dark:hover:bg-brand-dark/10 transition-all duration-300"
            >
              <ArrowLeft size={22} className="text-brand-light dark:text-brand-dark" strokeWidth={1.5} />
            </button>
            <div className="flex-1 flex items-center">
              <h1 className="text-xl font-serif font-bold text-brand-light dark:text-brand-dark tracking-wide leading-none">
                {selectedCategory?.name || 'Serviços'}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div ref={servicesScrollRef} className="flex-1 overflow-y-auto mobile-scroll bg-brand-light dark:bg-brand-dark" style={{ paddingTop: '70px' }}>
        {/* Main Content */}
        <div className="bg-brand-light dark:bg-brand-dark px-4 sm:px-6 py-3 min-h-0">
        {/* Loading State */}
        {isLoadingServices && (
          <div className="flex justify-center items-center py-20">
            <Loader2 size={32} className="spinner text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
          </div>
        )}

        {/* Error State */}
        {serviceError && (
          <div className="text-center py-20">
            <p className="text-red-600 dark:text-red-400 mb-6 font-sans">{serviceError}</p>
            <button
              onClick={retryLoadServices}
              className="px-8 py-3 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium tracking-wider uppercase text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
            >
              Tentar novamente
            </button>
          </div>
        )}


        {/* Services List */}
        {!isLoadingServices && !serviceError && (
          <div className={`space-y-4 ${selectedServices.length > 0 ? 'pb-40' : 'pb-24'}`}>
            {fetchedServices.map((service) => {
              const isSelected = selectedServices.some(s => s.id === service.id);
              const isExpanded = isServiceExpanded(service.id);
              const serviceImages = buildServiceImages(service);
              const variations = serviceVariations[service.id] || [];
              const selectedVariations = getServiceVariations(service.id);
              // Always calculate final price with variations, whether selected or not
              const finalPrice = getServiceFinalPrice(service) || parseFloat(service.price);
              const priceRange = getPriceRange(service.price, variations);
              
              // Check if service or any of its variations have price subject to evaluation
              const serviceSubjectToEvaluation = service.price_subject_to_evaluation;
              const hasVariationsSubjectToEvaluation = variations.some(group => 
                group.variations?.some(v => v.price_subject_to_evaluation)
              );
              const isAnySubjectToEvaluation = serviceSubjectToEvaluation || hasVariationsSubjectToEvaluation;
              
              // Get formatted price information
              const priceInfo = formatPriceWithEvaluation(
                service.price, 
                isAnySubjectToEvaluation, 
                variations.length > 0
              );
              
              return (
                <div
                  key={service.id}
                  id={`service-card-${service.id}`}
                  className="bg-brand-light dark:bg-brand-dark border border-brand-dark/10 dark:border-brand-light/10 overflow-hidden mb-6"
                >
                  {/* Service Card Header - Always Visible */}
                  <div className="px-5 pt-5 pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide leading-tight">
                          {service.name}
                        </h3>
                      </div>

                    </div>
                  </div>

                  {/* Service Images Carousel - Luxury Magazine Style */}
                  {serviceImages.length > 0 && (
                    <div className="px-5 pt-0 pb-3">
                      <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
                        {serviceImages.map((image, index) => {
                          const imageKey = `${service.id}-${index}`;
                          const isLoading = imageLoadingStates[imageKey];
                          const hasError = imageErrors[imageKey];

                          return (
                            <div key={index} className="flex-shrink-0 relative group">
                              {/* Loading placeholder */}
                              {isLoading && (
                                <div className="absolute inset-0 w-28 h-28 bg-brand-gray-200 dark:bg-brand-gray-800 flex items-center justify-center">
                                  <Loader2 size={18} className="spinner text-brand-dark dark:text-brand-light opacity-50" strokeWidth={1.5} />
                                </div>
                              )}

                              {/* Error placeholder */}
                              {hasError && (
                                <div className="w-28 h-28 bg-brand-gray-100 dark:bg-brand-gray-900 flex items-center justify-center border border-brand-dark/10 dark:border-brand-light/10">
                                  <Eye size={18} className="text-brand-dark dark:text-brand-light opacity-30" strokeWidth={1.5} />
                                </div>
                              )}

                              {/* Image - Square aspect ratio */}
                              {!hasError && (
                                <img
                                  src={image.src}
                                  alt={image.caption}
                                  className={`w-28 h-28 object-cover cursor-pointer transition-opacity duration-300 ${
                                    isLoading ? 'opacity-0' : 'opacity-100'
                                  }`}
                                  onClick={() => handleImagePress(image.src)}
                                  onLoadStart={() => handleImageLoadStart(imageKey)}
                                  onLoad={() => handleImageLoad(imageKey)}
                                  onError={() => handleImageError(imageKey)}
                                />
                              )}

                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Service Selection - Always Show Radio Buttons */}
                  <div className="px-4 pb-2">
                    {variations.length > 0 ? (
                      // Service with actual variations
                      <ServiceVariations
                        variationGroups={variations}
                        selectedVariations={selectedVariations}
                        onVariationSelect={(groupId, variation) => handleVariationSelect(service.id, groupId, variation)}
                        onVariationToggle={(groupId, variation) => handleVariationToggle(service.id, groupId, variation)}
                        size="small"
                        showGroupNames={false}
                        basePrice={parseFloat(service.price)}
                        baseDuration={service.duration_minutes || 0}
                        serviceSubjectToEvaluation={isAnySubjectToEvaluation}
                      />
                    ) : (
                      // Service without variations - show base service option
                      <ServiceVariations
                        variationGroups={[{
                          id: 'base',
                          name: 'Base Service',
                          service_id: service.id,
                          variations: [{
                            id: 'base',
                            name: service.name,
                            price_delta: 0,
                            duration_delta: 0,
                            display_order: 0,
                            price_subject_to_evaluation: service.price_subject_to_evaluation || false,
                            service_variation_group_id: 'base'
                          }]
                        }]}
                        selectedVariations={isSelected ? { 'base': { id: 'base' } } : {}}
                        onVariationSelect={(groupId, variation) => {
                          // Handle base service selection - toggle on/off
                          // If variation is null, we're unselecting, otherwise selecting
                          if (variation === null && isSelected) {
                            toggleService(service); // Unselect
                          } else if (variation !== null && !isSelected) {
                            toggleService(service); // Select
                          }
                        }}
                        onVariationToggle={() => {}} // Not used for base services
                        size="small"
                        showGroupNames={false}
                        basePrice={parseFloat(service.price)}
                        baseDuration={service.duration_minutes || 0}
                        serviceSubjectToEvaluation={isAnySubjectToEvaluation}
                      />
                    )}
                  </div>

                  {/* Click to see description - Luxury Button */}
                  <div className="px-5 pb-5 pt-2 border-t border-brand-dark/10 dark:border-brand-light/10">
                    <button
                      onClick={() => {
                        // Set service for the bottom sheet and open it
                        setSelectedServiceForSheet(service);
                        setIsServiceDetailsSheetVisible(true);
                        // Close the expanded card if it was open
                        setExpandedServiceIds(new Set());
                      }}
                      className="w-full flex items-center justify-center space-x-2 py-3 text-brand-dark dark:text-brand-light border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
                    >
                      <Eye size={16} strokeWidth={1.5} />
                      <span className="text-xs tracking-wider uppercase font-medium">
                        Ver detalhes
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );

  // Main render function
  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      {currentScreen === 'categories' && renderCategoriesScreen()}
      {currentScreen === 'services' && renderServicesScreen()}

      {/* Service Cart Footer - Shows on services screen when items selected */}
      {currentScreen === 'services' && renderServiceCartFooter()}

      {/* Image Modal - Luxury Design */}
      {imageModalVisible && (
        <div className="fixed inset-0 bg-brand-dark/95 dark:bg-black/95 flex items-center justify-center z-50 backdrop-blur-sm" onClick={() => setImageModalVisible(false)}>
          <div className="relative max-w-2xl max-h-[80vh] m-4">
            <button
              onClick={() => setImageModalVisible(false)}
              className="absolute -top-12 right-0 p-3 text-brand-light hover:bg-brand-light/10 transition-all duration-300"
            >
              <X size={24} strokeWidth={1.5} />
            </button>
            <img
              src={selectedImageForModal}
              alt="Service"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      )}

      {/* Service Details Bottom Sheet */}
      {isServiceDetailsSheetVisible && selectedServiceForSheet && (
        <ServiceDetailsBottomSheet
          service={selectedServiceForSheet}
          variations={serviceVariations[selectedServiceForSheet.id] || []}
          isVisible={isServiceDetailsSheetVisible}
          onClose={() => setIsServiceDetailsSheetVisible(false)}
          onAddService={() => {
            toggleService(selectedServiceForSheet);
            // For services with variations, automatically select the first variation of each required group
            const variations = serviceVariations[selectedServiceForSheet.id] || [];
            if (variations.length > 0) {
              variations.forEach(group => {
                if (group.variations && group.variations.length > 0) {
                  // Auto-select first variation in each group
                  const firstVariation = group.variations[0];
                  setServiceVariation(selectedServiceForSheet.id, group.id, firstVariation);
                }
              });
            }
          }}
          onRemoveService={() => toggleService(selectedServiceForSheet)}
          isSelected={selectedServices.some(s => s.id === selectedServiceForSheet.id)}
          selectedVariations={getServiceVariations(selectedServiceForSheet.id)}
          onVariationSelect={(serviceId, groupId, variation) => handleVariationSelect(selectedServiceForSheet.id, groupId, variation)}
          onVariationToggle={(serviceId, groupId, variation) => handleVariationToggle(selectedServiceForSheet.id, groupId, variation)}
          serviceSubjectToEvaluation={selectedServiceForSheet.price_subject_to_evaluation}
        />
      )}
    </div>
  );
};

const HomePage = forwardRef(HomePageInner);

export default HomePage;